import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

// import mui
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Button } from "@mui/material";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import JButton from "../components/JButton";
import { useTranslation } from "react-i18next";
import DisplayError from "../components/DisplayError";

const mainUrl = "https://www.jas-vermittlung.de";
// const mainUrl = "http://localhost";

// Create an Axios instance with custom configurations
const api = axios.create({
  baseURL: mainUrl,
  timeout: 10000,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

const Registration = () => {
  const { t } = useTranslation();

  // Check if registration deadline has passed
  const isRegistrationClosed = () => {
    const now = new Date();
    const deadline = new Date("2025-09-20T15:00:00"); // September 20, 2025 at 15:00

    return now >= deadline;
  };

  // Get deadline date for display
  const getDeadlineDate = () => {
    const deadline = new Date("2025-09-20T15:00:00");
    return deadline.toLocaleDateString();
  };

  // Form state
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    birthday: "",
    phone: "",
    institution: "",
    faculty: "",
    level: "",
    address: "",
  });

  // File state
  const [files, setFiles] = useState({
    photoUrl: null,
    passportUrl: null,
  });

  // File names for display
  const [fileNames, setFileNames] = useState({
    passFileName: "",
    imgFileName: "",
  });

  // UI state
  const [code, setCode] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [errors, setErrors] = useState({});

  // File input refs
  const germanCertificate = useRef(null);
  const englishCertificate = useRef(null);
  const passFile = useRef(null);
  const imageFile = useRef(null);

  // Constants
  const knowledgeLevel = [t("signup.no"), "A1", "A2", "B1", "B2", "C1", "C2"];
  const paymentMethods = [
    t("signup.payment1"),
    t("signup.payment2"),
    t("signup.payment3"),
  ];
  const levels = ["2", "3", t("signup.level")];
  const reasons = [
    t("signup.reason1"),
    t("signup.reason2"),
    t("signup.reason3"),
    t("signup.reason4"),
  ];

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Handle file uploads
  const handleFileChange = (field, file) => {
    setFiles((prev) => ({ ...prev, [field]: file }));
    setFileNames((prev) => ({
      ...prev,
      [field === "passportUrl"
        ? "passFileName"
        : field === "photoUrl"
        ? "imgFileName"
        : field === "certificateDeUrl"
        ? "germanFileName"
        : "englishFileName"]: file ? file.name : "",
    }));
    // Clear error when file is selected
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // File input handlers
  const handleClickFile = (ref) => ref.current?.click();

  const handleChangeGermanCertificate = (event) => {
    const file = event.target.files[0];
    handleFileChange("certificateDeUrl", file);
  };

  const handleChangeEnglishCertificate = (event) => {
    const file = event.target.files[0];
    handleFileChange("certificateEnUrl", file);
  };

  const handleChangePassFile = (event) => {
    const file = event.target.files[0];
    handleFileChange("passportUrl", file);
  };

  const handleChangeImageFile = (event) => {
    const file = event.target.files[0];
    handleFileChange("photoUrl", file);
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "firstname",
      "lastname",
      "birthday",
      "email",
      "phone",
      "institution",
      "faculty",
      "level",
      "address",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].trim() === "") {
        newErrors[field] = t("signup.helper2");
      }
    });

    if (!files.passportUrl || !files.photoUrl) {
      newErrors.passportUrl = t("signup.helper2");
      newErrors.photoUrl = t("signup.helper2");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const uploadProduct = () => {
    setLoading(true);
    const formDataToSend = new FormData();

    // Append form data
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key]);
    });

    // Append files
    Object.keys(files).forEach((key) => {
      if (files[key]) {
        formDataToSend.append(key, files[key]);
      }
    });

    api
      .post("/api/registration.php", formDataToSend)
      .then((response) => {
        console.log(response);
        setCode(response.data.responseCode);

        if (response.data.responseCode === 1) {
          setMessage("Ma'lumotlar muvaffaqiyatli jo'natildi!");
        } else if (response.data.responseCode === 3) {
          setMessage("Ro'yxatda mavjud!");
        } else {
          setMessage("Ma'lumotlarni jo'natishda xatolik!");
        }

        setLoading(false);
      })
      .catch((error) => {
        console.error("API request failed:", error);
        // setMessage("Server xatoligi! Iltimos keyinroq urinib ko'ring.");
        setCode(error.response.data.responseCode);
        setLoading(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      uploadProduct();
    }
  };

  // Reset form
  const resetForm = () => {
    setCode(4);
    setMessage("");
    setFormData({
      firstname: "",
      lastname: "",
      email: "",
      birthday: "",
      phone: "",
      institution: "",
      faculty: "",
      level: "",
      address: "",
    });
    setFiles({
      photoUrl: null,
      passportUrl: null,
    });
    setFileNames({
      passFileName: "",
      imgFileName: "",
    });
    setErrors({});
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Custom styled components
  const FileUploadBox = ({
    title,
    fileName,
    onClick,
    onChange,
    error,
    required = false,
  }) => (
    <Box
      component="fieldset"
      sx={{
        border: `2px dashed ${error ? "#d32f2f" : "#3c5132"}`,
        borderRadius: 12,
        p: 2,
        mb: 2,
        position: "relative",
        transition: "all 0.3s ease",
        "&:hover": {
          borderColor: "#4caf50",
          backgroundColor: "rgba(76, 175, 80, 0.05)",
        },
      }}
    >
      <Typography
        component="legend"
        sx={{
          color: error ? "#d32f2f" : "#3c5132",
          fontSize: "0.9rem",
          fontWeight: 500,
          px: 1,
        }}
      >
        {title} {required && "*"}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mt: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          onClick={onClick}
          sx={{
            borderColor: "#3c5132",
            color: "#3c5132",
            borderRadius: "20px",
            "&:hover": {
              borderColor: "#4caf50",
              backgroundColor: "rgba(76, 175, 80, 0.1)",
            },
          }}
        >
          {t("signup.helper1")}
        </Button>
        {fileName && (
          <Chip
            label={fileName}
            size="small"
            sx={{
              backgroundColor: "#e8f5e8",
              color: "#3c5132",
              maxWidth: "200px",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        )}
      </Box>
      {error && (
        <Typography
          variant="caption"
          sx={{ color: "#d32f2f", mt: 1, display: "block" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );

  // Form progress indicator
  const FormProgress = () => {
    const totalFields = 9; // Personal info (6) + Educational (3) + File (2)
    const totalFiles = 2;
    const filledFields = Object.values(formData).filter(
      (value) => value && value.trim() !== ""
    ).length;
    const filledFiles = Object.values(files).filter(
      (value) => value !== null && value !== undefined
    ).length;

    const progress = Math.round(
      ((filledFields + filledFiles) / (totalFields + totalFiles)) * 100
    );

    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t("signup.field19")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {progress}%
          </Typography>
        </Box>
        <Box
          sx={{
            width: "100%",
            bgcolor: "#e0e0e0",
            borderRadius: 10,
            height: 8,
          }}
        >
          <Box
            sx={{
              width: `${progress}%`,
              bgcolor: "#3c5132",
              borderRadius: 10,
              height: 8,
              transition: "width 0.3s ease",
            }}
          />
        </Box>
      </Box>
    );
  };

  // Registration closed component
  const RegistrationClosed = () => (
    <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>{t("signup.deadline_title")}</AlertTitle>
        {t("signup.deadline_message", { date: getDeadlineDate() })}
      </Alert>
    </Paper>
  );

  return (
    <div className="registration-container">
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box className="section_container">
          {/* Check if registration is closed */}
          {isRegistrationClosed() ? (
            <RegistrationClosed />
          ) : (
            <>
              {code === 1 && (
                <Paper
                  elevation={3}
                  className="success-message"
                  sx={{ p: 3, mb: 3 }}
                >
                  <Alert severity="success">
                    <AlertTitle>{t("signup.title2")}</AlertTitle>
                    {t("signup.field18")}
                  </Alert>
                  <Box className="btn_container">
                    <Button
                      className="btn_contact"
                      onClick={() => {
                        setCode(null);
                      }}
                    >
                      {t("signup.title3")}
                    </Button>
                  </Box>
                </Paper>
              )}

              {code !== 1 && <DisplayError code={code} message={message} />}

              {/* Loading Indicator */}
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                  <CircularProgress size={40} sx={{ color: "#3c5132" }} />
                </Box>
              )}

              {/* Main Form */}
              {code !== 1 && (
                <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
                  <Typography
                    variant="h4"
                    sx={{ textAlign: "center", mb: 3, color: "#3c5132" }}
                  >
                    {t("signup.title1")}
                  </Typography>

                  <FormProgress />

                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      {/* Personal Information */}
                      <Grid item xs={12}>
                        <Box className="section-divider">
                          <Typography
                            variant="h6"
                            sx={{
                              textAlign: "center",
                              color: "#3c5132",
                              fontWeight: 600,
                            }}
                          >
                            {t("signup.field20")}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label={t("signup.field1")}
                          value={formData.firstname}
                          onChange={(e) =>
                            handleInputChange("firstname", e.target.value)
                          }
                          error={!!errors.firstname}
                          helperText={errors.firstname}
                          sx={{ mb: 2 }}
                          inputProps={{
                            "aria-describedby": errors.firstname
                              ? "firstname-error"
                              : undefined,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label={t("signup.field2")}
                          value={formData.lastname}
                          onChange={(e) =>
                            handleInputChange("lastname", e.target.value)
                          }
                          error={!!errors.lastname}
                          helperText={errors.lastname}
                          sx={{ mb: 2 }}
                          inputProps={{
                            "aria-describedby": errors.lastname
                              ? "lastname-error"
                              : undefined,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          type="date"
                          label={t("signup.field3")}
                          value={formData.birthday}
                          onChange={(e) =>
                            handleInputChange("birthday", e.target.value)
                          }
                          error={!!errors.birthday}
                          helperText={errors.birthday}
                          InputLabelProps={{ shrink: true }}
                          sx={{ mb: 2 }}
                          inputProps={{
                            "aria-describedby": errors.birthday
                              ? "birthday-error"
                              : undefined,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label={t("signup.field17")}
                          value={formData.address}
                          onChange={(e) =>
                            handleInputChange("address", e.target.value)
                          }
                          error={!!errors.address}
                          helperText={errors.address}
                          sx={{ mb: 2 }}
                          inputProps={{
                            "aria-describedby": errors.address
                              ? "address-error"
                              : undefined,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          type="email"
                          label={t("signup.field5")}
                          value={formData.email}
                          onChange={(e) =>
                            handleInputChange("email", e.target.value)
                          }
                          error={!!errors.email}
                          helperText={errors.email}
                          sx={{ mb: 2 }}
                          inputProps={{
                            "aria-describedby": errors.email
                              ? "email-error"
                              : undefined,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label={t("signup.field6")}
                          value={formData.phone}
                          onChange={(e) =>
                            handleInputChange("phone", e.target.value)
                          }
                          error={!!errors.phone}
                          helperText={errors.phone}
                          sx={{ mb: 2 }}
                          inputProps={{
                            "aria-describedby": errors.phone
                              ? "phone-error"
                              : undefined,
                          }}
                        />
                      </Grid>

                      {/* Educational Information */}
                      <Grid item xs={12}>
                        <Box className="section-divider">
                          <Typography
                            variant="h6"
                            sx={{
                              textAlign: "center",
                              color: "#3c5132",
                              fontWeight: 600,
                            }}
                          >
                            {t("signup.field21")}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label={t("signup.field7")}
                          value={formData.institution}
                          onChange={(e) =>
                            handleInputChange("institution", e.target.value)
                          }
                          error={!!errors.institution}
                          helperText={errors.institution}
                          sx={{ mb: 2 }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          label={t("signup.field8")}
                          value={formData.faculty}
                          onChange={(e) =>
                            handleInputChange("faculty", e.target.value)
                          }
                          error={!!errors.faculty}
                          helperText={errors.faculty}
                          sx={{ mb: 2 }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          required
                          select
                          label={t("signup.field9")}
                          value={formData.level}
                          onChange={(e) =>
                            handleInputChange("level", e.target.value)
                          }
                          error={!!errors.level}
                          helperText={errors.level}
                          sx={{ mb: 2 }}
                        >
                          {levels.map((item, i) => (
                            <MenuItem key={i} value={item}>
                              {item}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      {/* File Uploads */}
                      <Grid item xs={12}>
                        <Box className="section-divider">
                          <Typography
                            variant="h6"
                            sx={{
                              textAlign: "center",
                              color: "#3c5132",
                              fontWeight: 600,
                            }}
                          >
                            {t("signup.field22")}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6} className="file-upload-section">
                        <FileUploadBox
                          title={t("signup.field13")}
                          fileName={fileNames.passFileName}
                          onClick={() => handleClickFile(passFile)}
                          onChange={handleChangePassFile}
                          error={errors.passportUrl}
                          required={true}
                        />
                        <input
                          type="file"
                          onChange={handleChangePassFile}
                          ref={passFile}
                          style={{ display: "none" }}
                          accept=".pdf,.jpg,.jpeg,.png"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} className="file-upload-section">
                        <FileUploadBox
                          title={t("signup.field14")}
                          fileName={fileNames.imgFileName}
                          onClick={() => handleClickFile(imageFile)}
                          onChange={handleChangeImageFile}
                          error={errors.photoUrl}
                        />
                        <input
                          type="file"
                          onChange={handleChangeImageFile}
                          ref={imageFile}
                          style={{ display: "none" }}
                          accept=".jpg,.jpeg,.png"
                        />
                      </Grid>

                      {/* Submit Button */}
                      <Grid item xs={12}>
                        <Box sx={{ textAlign: "center", mt: 3 }}>
                          <JButton
                            type="primary"
                            text="signup.send"
                            onClick={handleSubmit}
                            disabled={loading}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              )}

              <DisplayError code={code} message={message} />
            </>
          )}
        </Box>
      </Container>
    </div>
  );
};

export default Registration;
